import { useCallback, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { getPermalink } from '@atlassian/bitkit-diff/exports/diff-permalink';

import {
  getGlobalIsColorBlindModeEnabled,
  getGlobalIsAnnotationsEnabled,
  getGlobalDiffViewMode,
  getGlobalTabSize,
  DiffViewMode,
} from 'src/redux/diff-settings';
import { showFlagComponent } from 'src/redux/flags';
import {
  ACTIVE_DIFF_PERMALINK,
  toggleSideBySideMode,
} from 'src/redux/pull-request/actions';
import {
  getActivePermalink,
  getCurrentPullRequestAnnotationsForFile,
  getHiddenDiffComments,
  getSideBySideDiffStateForFile,
} from 'src/redux/pull-request/selectors';
import { SYNTAX_HIGHLIGHTING_FLAG_ID } from 'src/sections/global/components/flags/syntax-highlighting-flag';
import { getSyntaxHighlightingEnabled } from 'src/selectors/feature-selectors';
import { Diff as DiffType } from 'src/types/pull-request';
import { BucketState } from 'src/types/state';
import { extractFilepath } from 'src/utils/extract-file-path';
import store from 'src/utils/store';

export const useAnnotations = (diff: DiffType) => {
  const isPrAnnotationsEnabled = useSelector(getGlobalIsAnnotationsEnabled);
  const fileAnnotations = useSelector((state: BucketState) =>
    getCurrentPullRequestAnnotationsForFile(state, diff.to)
  );

  return {
    fileAnnotations: isPrAnnotationsEnabled ? fileAnnotations : undefined,
    isPrAnnotationsEnabled,
  };
};

export const useGlobalDiffPreferences = () => {
  const isColorBlindModeEnabled = useSelector(getGlobalIsColorBlindModeEnabled);
  const isSideBySide =
    useSelector(getGlobalDiffViewMode) === DiffViewMode.SideBySide;
  const tabSize = useSelector(getGlobalTabSize);

  return {
    isColorBlindModeEnabled,
    isSideBySide,
    tabSize,
  };
};

export const useHiddenComments = (diff: DiffType | string | null) => {
  const hiddenDiffConversations = useSelector(getHiddenDiffComments) || [];
  const filePath =
    (diff && (typeof diff === 'string' ? diff : extractFilepath(diff))) || '';
  return hiddenDiffConversations.includes(filePath);
};

export const useDiffPreferences = (diff: DiffType | null) => {
  const globalPreferences = useGlobalDiffPreferences();

  const dispatch = useDispatch();
  const path = diff && extractFilepath(diff);

  const isSideBySide = useSelector((state: BucketState) =>
    path
      ? getSideBySideDiffStateForFile(state, path)
      : globalPreferences.isSideBySide
  );

  const toggleIsSideBySide = useCallback(
    () =>
      path
        ? dispatch(
            toggleSideBySideMode({
              filePath: path,
              isSideBySide: !isSideBySide,
            })
          )
        : null,
    [dispatch, isSideBySide, path]
  );

  return {
    ...globalPreferences,
    isSideBySide,
    toggleIsSideBySide,
  };
};

export const usePermalinks = () => {
  const dispatch = useDispatch();
  const activePermalinkFromStore = useSelector(getActivePermalink);
  const activePermalinkFromWindowLocation = getPermalink();
  const activePermalink =
    activePermalinkFromStore || activePermalinkFromWindowLocation;
  const onPermalinkClick = useCallback(
    (permalink: string) =>
      dispatch({
        type: ACTIVE_DIFF_PERMALINK,
        payload: permalink,
      }),
    [dispatch]
  );

  return { activePermalink, onPermalinkClick, showPermalinks: true };
};

const SYNTAX_HIGHLIGHTING_FLAG_COUNT_LOCAL_STORE_ID =
  'syntax-highlighting-flag-count';

const SYNTAX_HIGHLIGHTING_FLAG_TIMESTAMP_LOCAL_STORE_ID =
  'syntax-highlighting-flag-timestamp';

const DAY_IN_MILLISECONDS = 86400000;
const MAX_HIGHLIGHT_SYNTAX_NOTIFICATIONS = 3;

export const useShowSyntaxHighlightingFlag = (): void => {
  const dispatch = useDispatch();
  const timestampStr = store.get(
    SYNTAX_HIGHLIGHTING_FLAG_TIMESTAMP_LOCAL_STORE_ID
  );
  const lastNotificationTime = timestampStr ? parseInt(timestampStr, 10) : 0;
  const countStr = store.get(SYNTAX_HIGHLIGHTING_FLAG_COUNT_LOCAL_STORE_ID, 0);
  const notificationCount = countStr ? parseInt(countStr, 10) : 0;
  const isSyntaxHighlightingEnabled = useSelector(getSyntaxHighlightingEnabled);

  useEffect(() => {
    const lastNotificationDate = new Date(lastNotificationTime);
    const timeSinceLastNotification =
      new Date().getTime() - lastNotificationDate.getTime();
    if (
      notificationCount < MAX_HIGHLIGHT_SYNTAX_NOTIFICATIONS &&
      timeSinceLastNotification > DAY_IN_MILLISECONDS &&
      !isSyntaxHighlightingEnabled
    ) {
      dispatch(showFlagComponent(SYNTAX_HIGHLIGHTING_FLAG_ID));
      store.set(
        SYNTAX_HIGHLIGHTING_FLAG_TIMESTAMP_LOCAL_STORE_ID,
        new Date().getTime()
      );
      store.set(
        SYNTAX_HIGHLIGHTING_FLAG_COUNT_LOCAL_STORE_ID,
        notificationCount + 1
      );
    }
  }, [
    dispatch,
    notificationCount,
    lastNotificationTime,
    isSyntaxHighlightingEnabled,
  ]);
};
